// Hook moved from `src/hooks/query/useQueryProducts.ts` (now a delegate).
// Pricing helpers stay app-side: they read `@heidi/commerce/contracts`.

import { skipToken, useQuery } from '@tanstack/react-query';
import { GET_PRODUCTS, type SubscriptionTier } from '../../contracts';
import { getHeidi2Products, getProducts } from '../api/products';
import { getCommerceDataConfig } from '../config';

export type ProductCatalogueVersion = 'v2' | 'v3';

export type UseQueryProductsQueryParams = {
  /**
   * @deprecated No-op. `/products` ignores `new_product_tiers` and always
   * returns the full catalogue; passing it used to fragment the cache.
   * Filter client-side with `getNewProductTierProduct` / `getTierProduct`.
   */
  new_product_tiers?: SubscriptionTier[] | null;
};

export type UseQueryProductsProps = {
  /**
   * `Boolean(useQueryUser().data)`, resolved app-side. The query gated on the
   * user being loaded, not on anything the user contains, so a boolean is the
   * whole dependency — passing the user object would create a commerce →
   * workspace coupling that buys nothing.
   */
  hasUser: boolean;
  userRegionSameAsKindeRegion: boolean;
  catalogueVersion?: ProductCatalogueVersion;
};

/**
 * GET /products — the full catalogue.
 *
 * Both gates are preserved with their original asymmetry, which is easy to
 * "tidy" and wrong to: `enabled` (token + user) makes the query WAIT, while
 * the region mismatch uses `skipToken` so it never runs at all. Collapsing
 * them into one `enabled` would let a region-mismatched user's query become
 * live the moment the other condition flipped.
 */
export function useQueryProducts(
  {
    hasUser,
    userRegionSameAsKindeRegion,
    catalogueVersion = 'v2',
  }: UseQueryProductsProps,
  // Accepted but intentionally unused — kept so existing call sites that still
  // pass `{ new_product_tiers }` compile unchanged. The value is deliberately
  // ignored (see the queryKey note below and the type's @deprecated tag).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  queryParams?: UseQueryProductsQueryParams
) {
  const { useAuthToken } = getCommerceDataConfig();
  const token = useAuthToken();

  return useQuery({
    enabled: !!token && hasUser,
    // Excludes `new_product_tiers` — backend ignores it; keying fragmented the cache.
    queryKey: [GET_PRODUCTS, catalogueVersion],
    queryFn: userRegionSameAsKindeRegion
      ? async () => {
          const { data } =
            catalogueVersion === 'v3' ? await getHeidi2Products() : await getProducts();
          if (data) return data.products;
        }
      : skipToken,
  });
}
